import {
  AirtableMissionPayload,
  AirtableSociety,
  CRAStatus,
  ContractLegalStatus,
  AirtableNotification,
} from './airtable';
import { FileUpload, Upload } from 'graphql-upload';
import { TemplateContract, IMission, IPricing } from './types.shared';
import {
  InvoiceStatus,
  BusinessType,
  DealType,
  JobType,
  LostReasonType,
  StatusType,
  Staff,
  TalentBoardJobFunnelPhase,
} from './domain';
import { BackgroundType, ProfileNetwork } from './talent';

export type { FileUpload };

// In graphql data that can be undefined, is always returned as null
export type Maybe<T> = T | null;

// related to type Mision in schema.bloomers.graphql and ALL_MISSIONS gql query
export type AllMissionGQLItemResult = {
  recordId: string;
  label: Maybe<string>;
  beginMission: string;
  endMission: string;
  createdAt: string;
  bloomerFullName: Maybe<string[]>;
  clientSocietyName: string[];
  bloomerSocietyName: Maybe<string[]>;
  isActive: Maybe<boolean>;
  replacement: Maybe<boolean>;
  type: Maybe<BusinessType>;
  pricing: Maybe<IPricing[]>;
};

// see ALL_MISSIONS_WITH_EXTENSION gql query
export interface AllMissionWithExtensionGQLItemResult extends AllMissionGQLItemResult {
  extension: Maybe<Pick<ExtensionAssessmentGQL, 'assessment' | 'newEndDate'>>;
}

export type AllMissionGraphQLQueryParams =
  | {
      isActive: boolean;
      missionIds?: string[];
    }
  | undefined;

export type AllMissionGQLResult<T> = {
  count: number;
  results: T[];
};

export type UpdateMissionGQLInput = {
  recordId: string;
} & Partial<AirtableMissionPayload>;

export type UpsertExtensionAssessmentGQLInput = {
  missionId: string;
  assessment: number;
  newEndDate: Date;
};

export type ExtensionAssessmentGQL = {
  missionId: string;
  assessment: number;
  updatedAt: Date;
  newEndDate: Date;
  updatedBy: string;
};

export type CreateMissionAmendmentGQLInput = {
  newEndMission: Maybe<string>;
  recordId: string;
  useTemplateForSST: boolean;
  useTemplateForPDS: boolean;
  newPurchasePrice: Maybe<number>;
  newSalePrice: Maybe<number>;
  newPricingDate: Maybe<string>;
  firstClause: string;
  secondClauseForSST: string;
  secondClauseForPDS: string;
};

export type ShortenMissionGQLInput = {
  endMission: string;
  recordId: string;
};

export type GetExtensionAssessmentGQLResult = {
  getExtensionAssessment: Maybe<ExtensionAssessmentGQL>;
};

export type CRADashboardItemGQL = {
  missionId: string;
  bloomerFullName: string;
  clientSocietyName: string;
  contactOperationsName: string;
  activityId: Maybe<string>;
  activityMonth: Maybe<Date>;
  status: CRAStatus;
  craURL: Maybe<string>;
  invoiceURL: Maybe<string>;
  signingDocumentId: Maybe<string>;
  bloomerPayDateDays: Maybe<number>;
  dateUploadInvoice: Maybe<Date>;
  bloomerInvoiceBilled: boolean;
  bloomerInvoiceStatus: InvoiceStatus;
  bloomerInvoiceLastError: Maybe<string>;
  bloomerSocietyName: Maybe<string>;
  clientInvoiceStatus: InvoiceStatus;
  clientInvoiceLastError: Maybe<string>;
  bloomerScore: Maybe<number>;
  bloomerComment: Maybe<string>;
  bloomerScoreDate: Maybe<Date>;
  clientScore: Maybe<number>;
  clientComment: Maybe<string>;
  clientScoreDate: Maybe<Date>;
  nbDaysWorked: Maybe<number>;
  nbDaysMarkupWorked: Maybe<number>;
  nbDaysMarkup2Worked: Maybe<number>;
  nbDaysAstreinteWorked: Maybe<number>;
  nbDaysDetails: Maybe<string>;
  hoursPerDays: Maybe<number>;
};

export type FileUploadedGQL = {
  path: string;
  url: string;
};

export type FileUploadGQL = Upload;

export type UploadGQLInput = FileUploadGQL | FileUploadedGQL;

export interface SocietyGQLInput
  extends Omit<AirtableSociety, 'KBIS' | 'URSSAFVigilanceCertificate' | 'RCPVigilanceCertificate'> {
  KBIS?: UploadGQLInput;
  URSSAFVigilanceCertificate?: UploadGQLInput;
  RCPVigilanceCertificate?: UploadGQLInput;
}

export type LegalContractGQL = {
  id: number;
  recordId: string;
  nbSigner: number;
  signerDocId1: Maybe<string>;
  createdAt: Date;
  createdDate: string;
  signedCount: number;
  signedDate: Maybe<string>;
  smartFields?: Maybe<string>;
  status: ContractLegalStatus;
  provider: Maybe<string>;
  worflowExternalId?: string;
  contractTemplate: Maybe<string>;
  type: TemplateContract;
  step: string;
  emptyFileUrl: Maybe<string>;
  signedFileUrl: Maybe<string>;
  externalDocumentId: Maybe<string>;
  refusedReason: Maybe<string>;
  signLink: Maybe<string>;
};

export enum MissionStatusFilter {
  ACTIVE = 'ACTIVE',
  FINISHED = 'FINISHED',
  INCOMING = 'INCOMING',
}

export type MissionDataForGetLegalContractsResult = {
  id: string;
  label: string;
  bloomerId: string;
  bloomerFullName: string;
  clientSocietyId: string;
  clientSocietyName: string;
  bloomerSocietyName: Maybe<string>;
};

export type ContractsByMissionGQL = {
  mission: MissionDataForGetLegalContractsResult;
  contracts: LegalContractGQL[];
};

export type GetLegalContractsByMissionGQLResult = {
  getLegalContractsByMission: ContractsByMissionGQL;
};

export type GetLegalContractsGQLResult = {
  getLegalContracts: ContractsByMissionGQL[];
};

export type GetLegalContractsGQLParams = {
  status: MissionStatusFilter;
};

export type UploadAmendmentGQLInput = {
  file: Upload;
  legalContractRecordId: string;
  missionRecordId: string;
  signed: boolean;
  createInvitation: boolean;
  signedDate?: string;
};

export type UploadAmendmentGQLResult = {
  uploadAmendment: {
    recordId: string;
    type: TemplateContract;
    step: string;
    status: ContractLegalStatus;
    nbSigner: number;
    signedCount: number;
    emptyFileUrl: Maybe<string>;
    signedFileUrl: Maybe<string>;
    signedDate: Maybe<Date>;
    externalDocumentId: string;
  };
};

export type GetOneFullMissionParams = {
  recordId: string;
};

export type GetOneFullMissionResponse = {
  missions: IMission[];
};

export type SyncCandidatJobInput = {
  jobId: string;
  talentId: string;
  author: string;
};

export type QueryResult<T> = { count: number; results: T[] };
export type IMissionQueryResult = QueryResult<Partial<IMission>>;

export type GqlSociety = {
  name: string;
  siret?: Maybe<string>;
};

export type GqlAccountManager = {
  firstname: string;
  lastname: string;
  email: string;
  role: Staff;
  avatarUrl: Maybe<string>;
  phoneNumber: Maybe<string>;
  externalId: Maybe<string>;
};

// requested fields defined by JOB_FUNNEL_BASE_FIELDS
export interface GqlJobFunnel {
  id: string;
  name: string;
  jobType: JobType;
  dealType: Maybe<DealType>;
  jobTitle: string;
  society: Pick<GqlSociety, 'name'>;
  accountManager?: Maybe<Pick<GqlAccountManager, 'email' | 'firstname' | 'lastname'>>;
  team?: Maybe<string>;
  context?: Maybe<string>;
  location?: Maybe<string>;
  duration?: Maybe<number>;
  onSiteDaysPerWeek?: Maybe<number>;
  remoteDaysPerWeek?: Maybe<number>;
  expectedStartDate?: Maybe<string>;
  details?: Maybe<string>;
  scoreCard1?: Maybe<string>;
  scoreCard2?: Maybe<string>;
  scoreCard3?: Maybe<string>;
  scoreCard4?: Maybe<string>;
  externalId?: Maybe<string>;
  minYearsXP?: Maybe<number>;
  maxClientBudget?: Maybe<number>;
  clientBudgetDetails?: Maybe<string>;
  maxTalentBudget?: Maybe<number>;
  outOfBudgetProfil?: Maybe<string>;
  budgetDecisionMaker?: Maybe<string>;
  isInternalValidation?: Maybe<boolean>;
  clientResearchTrigger?: Maybe<string>;
  interviewProcess?: Maybe<string>;
  finalValidationResponsable?: Maybe<string>;
  minTotalProfilesToInclude?: Maybe<string>;
  searchStatus?: Maybe<string>;
  competitors?: Maybe<string>;
  isExclusivity?: Maybe<boolean>;
  cvDebriefDate?: Maybe<string>;
  isResponsiveClient?: Maybe<boolean>;
  isReliableClient?: Maybe<boolean>;
  isDirectLinkWithDecisionMaker?: Maybe<boolean>;
  priority?: Maybe<string>;
  isBloomerReplacement?: Maybe<boolean>;
  isQuickContract?: Maybe<boolean>;
  status?: Maybe<StatusType>;
  lostReason?: Maybe<LostReasonType>;
  otherLostReason?: Maybe<string>;
  bloomerSource?: Maybe<string>;
  commercialMonth?: Maybe<Date>;
  passeur?: Maybe<string>;
  buteur?: Maybe<string>;
  capitaine?: Maybe<string>;
  defenseur?: Maybe<string>;
  requiredLanguages?: Maybe<string>;
  interlocuteur1Email?: Maybe<string>;
  interlocuteur2Email?: Maybe<string>;
  createdAt?: Maybe<Date>;
  hubspotDealId?: Maybe<string>;
  airtableId?: Maybe<string>;
  pipefyId?: Maybe<string>;
  hiresweetId?: Maybe<string>;
  winDate?: Maybe<Date>;
}

export interface GqlDealJobFunnel extends GqlJobFunnel {
  leadOrigin?: Maybe<string>;
  leadCustomerStatus?: Maybe<string>;
  leadCanal?: Maybe<string>;
  leadCanalDetail?: Maybe<string>;
}

export type GqlTalent = {
  id: string;
  firstname: string;
  lastname: string;
  email: Maybe<string>;
  jobTitle: string;
};

export type GqlTalentForTalentBoard = GqlTalent & {
  image: Maybe<string>;
  availabilityDate: Maybe<Date>;
  location: Maybe<{
    id: string;
    city: string;
  }>;
  tjm: Maybe<string>;
  package?: Maybe<string>;
  profiles: GqlProfile[];
  cv: Maybe<string>;
  jobs: GqlJobTalent[];
  educations: GqlEducation[];
  languages: GqlLanguage[];
  phase: TalentBoardJobFunnelPhase;
  phaseNotes: GqlTalentPhaseNote[];
  topProfile: boolean;
  cardTamComment: Maybe<string>;
  generalTamEvaluation: Maybe<string>;
  comments: GqlTalentBoardComment[];
  addedInProject: Maybe<Date>;
  markedAsSeen: boolean;
  dateMarkedAsSeen: Maybe<Date>;
  becameBloomer: boolean;
};

export type GqlTalentJobFunnel = {
  talentId: string;
  jobId: string;
  topProfile: boolean;
  addedInProject: Maybe<Date>;
  markedAsSeen: boolean;
  dateMarkedAsSeen: Maybe<Date>;
};

export type GqlProfile = {
  id: Maybe<string>;
  network: Maybe<ProfileNetwork | string>;
  url: Maybe<string>;
  username: Maybe<string>;
  talentId: Maybe<string>;
};

export type GqlTalentPhaseNote = {
  id: string;
  phaseId: string;
  date: Date;
  answers: Record<string, string>;
  jobId: string;
  talentId: string;
  source: string;
  externalId: string;
};

export type GqlTalentBoardComment = {
  id: string;
  jobId: string;
  talentId: string;
  comment: string;
  author: string;
  createdAt: Date;
  updatedAt: Date;
};

export type GqlJobTalent = {
  id: Maybe<string>;
  jobTitle: { id: string; label: string; iso: boolean | null }[];
  period: Maybe<{
    id: string;
    startDate: Date;
    endDate: Maybe<Date>;
  }>;
  current: Maybe<boolean>;
  label: Maybe<string>;
  description: Maybe<string>;
  name: Maybe<string>;
  skills: Maybe<GqlSkill[]>;
};

export type GqlEducation = {
  id: Maybe<string>;
  name: string;
  label: Maybe<string>;
  description: Maybe<string>;
  current: Maybe<boolean>;
  url: Maybe<string>;
  type: Maybe<BackgroundType>;
  period: Maybe<{
    id: string;
    startDate: Date;
    endDate: Maybe<Date>;
  }>;
};

export type GqlLanguage = {
  id: Maybe<string>;
  label: string;
  level: Maybe<number>;
};

export type GqlSkill = {
  id: string;
  label: string;
  iso: boolean;
  alternateLabels: string[];
};

export type GqlJobFunnelWithBloomer = {
  job: GqlDealJobFunnel;
  talents: Maybe<GqlTalent[]>;
  bloomer: Maybe<GqlTalent>;
};

export type GetCurrentProjectsFilterInput = {
  role: 'tam' | 'client' | 'system' | 'anonymous';
  email: string;
};

export type GqlSynchroTalentFromATSResult = {
  success: boolean;
  errors: string[];
};

export type GqlSynchroTalentFromATSOutput = {
  talent: GqlSynchroTalentFromATSResult;
  resume: GqlSynchroTalentFromATSResult;
  notes: GqlSynchroTalentFromATSResult;
};

export type GqlTalentBoardInvitation = {
  id: string;
  jobId: string;
  email: string;
  createdAt: Date;
  createdBy: string;
  resentAt: Maybe<Date>;
};

// Ces types représentent ce qui est renvoyé par les requêtes Apollo et qui est vu côté front, donc il est fortement couplé à la techno

export type ApolloGqlAccountManager = GqlAccountManager & {
  __typename: 'GqlAccountManager';
};

export type ApolloGqlJobFunnel = Required<GqlJobFunnel> & {
  __typename: 'GqlJobFunnel';
} & {
  society: GqlJobFunnel['society'] & {
    __typename: 'GqlSociety';
  };
  accountManager: GqlJobFunnel['accountManager'] & {
    __typename: 'GqlAccountManager';
  };
};

export type ApolloGqlTalentForTalentBoard = Required<GqlTalentForTalentBoard> & {
  __typename: 'GqlTalentForTalentBoard';
  profiles: (Required<GqlProfile> & {
    __typename: 'GqlProfile';
  })[];
  jobs: (Required<GqlJobTalent> & {
    __typename: 'GqlJobTalent';
  })[];
  educations: (Required<GqlEducation> & {
    __typename: 'GqlEducation';
  })[];
  languages: (Required<GqlLanguage> & {
    __typename: 'GqlLanguage';
  })[];
  phaseNotes: (Required<GqlTalentPhaseNote> & {
    __typename: 'GqlTalentPhaseNote';
  })[];
};

export type ApolloGqlJobFunnelWithTalents = {
  __typename: 'GqlJobFunnelWithTalents';
  job: Required<GqlJobFunnelWithBloomer['job']> & {
    __typename: 'GqlDealJobFunnel';
  } & {
    society: GqlJobFunnel['society'] & {
      __typename: 'GqlSociety';
    };
    accountManager: GqlJobFunnel['accountManager'] & {
      __typename: 'GqlAccountManager';
    };
  };
  talents: (Required<GqlTalent> & {
    __typename: 'GqlTalent';
  })[];
  bloomer: Required<GqlTalent> & {
    __typename: 'GqlTalent';
  };
};

export type ApolloGqlTalentBoardStats = {
  contacted: number;
  __typename: 'GqlTalentBoardStats';
};

export type NotificationHistoryGQL = {
  id?: string;
  processedOn?: number;
  error?: string | null;
  progress?: number;
  data: AirtableNotification;
};
